import { useContext, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { XmppContext } from "@/stream/xmppClient"
import AdHocCommands from "@/api/adHocCommands"
import { uploadToS3 } from "@/components/analyzeOrderOnPage/uploadToS3"
import { parseRouteMetaData } from "@/hooks/useExtractDataFromOrder"
import type { FormFields } from "@/components/maps/orderDetails/OrderDetailsDialog"

export type RetrofitResult = {
  existingFields: FormFields
  fileUrl: string
  fileName: string
  extracted: NonNullable<ReturnType<typeof parseRouteMetaData>>
}

// existingFields is whatever the caller currently has on screen (including
// any not-yet-saved manual edits) — the compare step diffs against that, not
// a fresh re-fetch, so in-progress edits aren't silently dropped by upload.
export const useOriginalDocumentUpload = (driverJid: string) => {
  const { t } = useTranslation()
  const { client } = useContext(XmppContext)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<RetrofitResult | null>(null)

  const handleFile = async (file: File, existingFields: FormFields) => {
    if (!client) {
      setError(t("orders.orderDetails.notConnected"))
      return
    }
    setUploading(true)
    setError(null)
    try {
      const { getUrl, fileName } = await uploadToS3(file)
      const extractResult = await AdHocCommands.extractRouteFromOrders(
        client,
        driverJid,
        [{ url: getUrl, name: fileName, file }],
      )
      const extracted = parseRouteMetaData(extractResult?.getChild("command"))
      if (!extracted)
        throw new Error(t("orders.orderDetails.upload.noMetadata"))
      setResult({ existingFields, fileUrl: getUrl, fileName, extracted })
    } catch (err: any) {
      setError(err?.message ?? t("orders.orderDetails.upload.processFailed"))
    } finally {
      setUploading(false)
    }
  }

  const triggerFilePick = () => fileInputRef.current?.click()

  const reset = () => {
    setResult(null)
    setError(null)
  }

  return {
    fileInputRef,
    uploading,
    error,
    result,
    handleFile,
    triggerFilePick,
    reset,
  }
}
